/**
 * Waypoint - Ground-level runic stone circle
 * Inspired by POE/Diablo waypoints - a carved stone platform flush with the ground
 */

import { t } from '@locales/index';

export interface WaypointConfig {
  x: number;
  y: number;
  name: string;
  activated?: boolean;
}

export class Waypoint {
  private x: number;
  private y: number;
  private name: string;
  private animationTime: number = 0;
  private readonly RADIUS = 38;
  private activated: boolean;
  private activationAnim: number = 0;

  // Rune positions (pre-calculated angles for 8 runes)
  private readonly RUNE_COUNT = 8;

  constructor(config: WaypointConfig) {
    this.x = config.x;
    this.y = config.y;
    this.name = config.name;
    this.activated = config.activated ?? false;
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  isActivated(): boolean {
    return this.activated;
  }

  setActivated(activated: boolean): void {
    if (!this.activated && activated) {
      this.activationAnim = 2.5;
    }
    this.activated = activated;
  }

  update(deltaTime: number): void {
    this.animationTime += deltaTime;
    if (this.activationAnim > 0) {
      this.activationAnim = Math.max(0, this.activationAnim - deltaTime);
    }
  }

  render(ctx: CanvasRenderingContext2D, cameraX: number, cameraY: number): void {
    const sx = this.x - cameraX;
    const sy = this.y - cameraY;
    const t_ = this.animationTime;
    const R = this.RADIUS;
    const active = this.activated;

    ctx.save();

    // --- Activation burst ---
    if (this.activationAnim > 0) {
      this.renderActivationBurst(ctx, sx, sy, R, t_);
    }

    // --- Ground glow (activated only) ---
    if (active) {
      this.renderGroundGlow(ctx, sx, sy, R, t_);
    }

    // --- Outer stone ring (isometric ellipse, flush with ground) ---
    this.renderStoneBase(ctx, sx, sy, R, active, t_);

    // --- Rune symbols around the edge ---
    this.renderRunes(ctx, sx, sy, R, active, t_);

    // --- Center emblem ---
    this.renderCenterEmblem(ctx, sx, sy, active, t_);

    // --- Rising energy particles (activated) ---
    if (active) {
      this.renderEnergyParticles(ctx, sx, sy, R, t_);
    }

    // --- Label ---
    this.renderLabel(ctx, sx, sy, R, active);

    ctx.restore();
  }

  private renderActivationBurst(ctx: CanvasRenderingContext2D, sx: number, sy: number, R: number, _t: number): void {
    const progress = 1 - (this.activationAnim / 2.5);

    // Expanding ring
    const ringR = R + 80 * progress;
    const ringAlpha = (1 - progress) * 0.7;
    ctx.strokeStyle = `rgba(100, 200, 255, ${ringAlpha})`;
    ctx.lineWidth = 3 * (1 - progress);
    ctx.beginPath();
    ctx.ellipse(sx, sy, ringR, ringR * 0.45, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Inner burst glow
    const burstR = R + 50 * progress;
    const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, burstR);
    grad.addColorStop(0, `rgba(120, 200, 255, ${ringAlpha * 0.5})`);
    grad.addColorStop(0.5, `rgba(60, 140, 220, ${ringAlpha * 0.2})`);
    grad.addColorStop(1, 'rgba(40, 100, 200, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(sx, sy, burstR, 0, Math.PI * 2);
    ctx.fill();

    // Burst particles flying outward
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const dist = R * 0.5 + progress * 60;
      const px = sx + Math.cos(angle) * dist;
      const py = sy + Math.sin(angle) * dist * 0.45;
      const alpha = (1 - progress) * 0.8;
      const size = 2.5 * (1 - progress);

      ctx.fillStyle = `rgba(180, 220, 255, ${alpha})`;
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderGroundGlow(ctx: CanvasRenderingContext2D, sx: number, sy: number, R: number, t_: number): void {
    const pulse = 0.25 + Math.sin(t_ * 1.5) * 0.1;
    const grad = ctx.createRadialGradient(sx, sy, 0, sx, sy, R + 25);
    grad.addColorStop(0, `rgba(60, 160, 255, ${pulse})`);
    grad.addColorStop(0.5, `rgba(30, 100, 200, ${pulse * 0.4})`);
    grad.addColorStop(1, 'rgba(20, 60, 150, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(sx, sy, R + 25, (R + 25) * 0.45, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  private renderStoneBase(ctx: CanvasRenderingContext2D, sx: number, sy: number, R: number, active: boolean, _t: number): void {
    const isoRatio = 0.45; // isometric squash

    // Shadow under the stone
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.beginPath();
    ctx.ellipse(sx, sy + 4, R + 4, (R + 4) * isoRatio, 0, 0, Math.PI * 2);
    ctx.fill();

    // Outer stone ring
    ctx.fillStyle = active ? '#3a3a48' : '#2a2a2a';
    ctx.beginPath();
    ctx.ellipse(sx, sy, R + 2, (R + 2) * isoRatio, 0, 0, Math.PI * 2);
    ctx.fill();

    // Outer ring border
    ctx.strokeStyle = active ? '#5a5a6a' : '#3a3a3a';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(sx, sy, R + 2, (R + 2) * isoRatio, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Inner stone floor (slightly raised look)
    const innerR = R - 5;
    ctx.fillStyle = active ? '#2e2e3a' : '#222222';
    ctx.beginPath();
    ctx.ellipse(sx, sy, innerR, innerR * isoRatio, 0, 0, Math.PI * 2);
    ctx.fill();

    // Inner ring groove
    ctx.strokeStyle = active ? '#4a6a8a' : '#333333';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.ellipse(sx, sy, innerR, innerR * isoRatio, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Middle decorative ring
    const midR = R * 0.6;
    ctx.strokeStyle = active ? '#3a5a7a' : '#2a2a2a';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(sx, sy, midR, midR * isoRatio, 0, 0, Math.PI * 2);
    ctx.stroke();

    // Cross lines on the stone (carved grooves)
    ctx.strokeStyle = active ? 'rgba(60, 100, 150, 0.4)' : 'rgba(40, 40, 40, 0.5)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const innerDist = midR * 0.3;
      const outerDist = innerR * 0.95;
      ctx.beginPath();
      ctx.moveTo(sx + cos * innerDist, sy + sin * innerDist * isoRatio);
      ctx.lineTo(sx + cos * outerDist, sy + sin * outerDist * isoRatio);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(sx - cos * innerDist, sy - sin * innerDist * isoRatio);
      ctx.lineTo(sx - cos * outerDist, sy - sin * outerDist * isoRatio);
      ctx.stroke();
    }
  }

  private renderRunes(ctx: CanvasRenderingContext2D, sx: number, sy: number, R: number, active: boolean, t_: number): void {
    const isoRatio = 0.45;
    const runeR = R * 0.78;

    for (let i = 0; i < this.RUNE_COUNT; i++) {
      const angle = (i / this.RUNE_COUNT) * Math.PI * 2 - Math.PI / 2;
      const rx = sx + Math.cos(angle) * runeR;
      const ry = sy + Math.sin(angle) * runeR * isoRatio;

      if (active) {
        // Glowing rune
        const pulse = 0.6 + Math.sin(t_ * 2 + i * 0.8) * 0.3;

        // Rune glow
        ctx.fillStyle = `rgba(80, 170, 255, ${pulse * 0.4})`;
        ctx.beginPath();
        ctx.arc(rx, ry, 6, 0, Math.PI * 2);
        ctx.fill();

        // Rune symbol (small diamond)
        ctx.fillStyle = `rgba(120, 200, 255, ${pulse})`;
        ctx.beginPath();
        ctx.moveTo(rx, ry - 4);
        ctx.lineTo(rx + 3, ry);
        ctx.lineTo(rx, ry + 4);
        ctx.lineTo(rx - 3, ry);
        ctx.closePath();
        ctx.fill();

        // Bright center dot
        ctx.fillStyle = `rgba(200, 235, 255, ${pulse * 0.8})`;
        ctx.beginPath();
        ctx.arc(rx, ry, 1.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Dim carved rune
        ctx.fillStyle = 'rgba(60, 60, 60, 0.6)';
        ctx.beginPath();
        ctx.moveTo(rx, ry - 3);
        ctx.lineTo(rx + 2.5, ry);
        ctx.lineTo(rx, ry + 3);
        ctx.lineTo(rx - 2.5, ry);
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  private renderCenterEmblem(ctx: CanvasRenderingContext2D, sx: number, sy: number, active: boolean, t_: number): void {
    const isoRatio = 0.45;

    if (active) {
      // Center glow
      const pulse = 0.4 + Math.sin(t_ * 1.8) * 0.15;
      const cGrad = ctx.createRadialGradient(sx, sy, 0, sx, sy, 14);
      cGrad.addColorStop(0, `rgba(100, 190, 255, ${pulse})`);
      cGrad.addColorStop(1, 'rgba(50, 120, 200, 0)');
      ctx.fillStyle = cGrad;
      ctx.beginPath();
      ctx.ellipse(sx, sy, 14, 14 * isoRatio, 0, 0, Math.PI * 2);
      ctx.fill();

      // Center star emblem (6-pointed)
      const starPulse = 0.7 + Math.sin(t_ * 2.5) * 0.3;
      ctx.fillStyle = `rgba(140, 210, 255, ${starPulse})`;
      this.drawStar(ctx, sx, sy, 6, 8, 4, isoRatio);

      // Bright core
      ctx.fillStyle = `rgba(220, 240, 255, ${starPulse * 0.9})`;
      ctx.beginPath();
      ctx.ellipse(sx, sy, 3, 3 * isoRatio, 0, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Dim center emblem
      ctx.fillStyle = 'rgba(50, 50, 50, 0.5)';
      this.drawStar(ctx, sx, sy, 6, 7, 3.5, isoRatio);

      ctx.fillStyle = 'rgba(40, 40, 40, 0.6)';
      ctx.beginPath();
      ctx.ellipse(sx, sy, 2.5, 2.5 * isoRatio, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private drawStar(ctx: CanvasRenderingContext2D, cx: number, cy: number, points: number, outerR: number, innerR: number, isoRatio: number): void {
    ctx.beginPath();
    for (let i = 0; i < points * 2; i++) {
      const angle = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : innerR;
      const px = cx + Math.cos(angle) * r;
      const py = cy + Math.sin(angle) * r * isoRatio;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();
  }

  private renderEnergyParticles(ctx: CanvasRenderingContext2D, sx: number, sy: number, R: number, t_: number): void {
    const isoRatio = 0.45;

    // Rising energy wisps from the runes
    for (let i = 0; i < 8; i++) {
      const seed = i * 1.37;
      const lifespan = 2.5;
      const phase = ((t_ * 0.7 + seed) % lifespan) / lifespan;

      // Spawn position on the stone circle
      const spawnAngle = seed * 5.1;
      const spawnR = R * (0.3 + (seed % 1) * 0.5);
      const baseX = sx + Math.cos(spawnAngle) * spawnR;
      const baseY = sy + Math.sin(spawnAngle) * spawnR * isoRatio;

      // Rise upward with slight drift
      const riseHeight = phase * 35;
      const drift = Math.sin(t_ * 1.5 + seed * 3) * 5;
      const px = baseX + drift;
      const py = baseY - riseHeight;

      // Fade in then out
      const alpha = phase < 0.2 ? phase / 0.2 : (1 - phase) * 0.7;
      const size = 1.5 + (1 - phase) * 1.5;

      ctx.fillStyle = `rgba(120, 200, 255, ${alpha * 0.7})`;
      ctx.beginPath();
      ctx.arc(px, py, size, 0, Math.PI * 2);
      ctx.fill();
    }

    // Orbiting particles at ground level
    for (let i = 0; i < 4; i++) {
      const angle = t_ * 0.8 + (i / 4) * Math.PI * 2;
      const orbitR = R * 0.85;
      const px = sx + Math.cos(angle) * orbitR;
      const py = sy + Math.sin(angle) * orbitR * isoRatio;
      const alpha = 0.4 + Math.sin(t_ * 3 + i * 2) * 0.2;

      ctx.fillStyle = `rgba(100, 180, 255, ${alpha})`;
      ctx.beginPath();
      ctx.arc(px, py, 2, 0, Math.PI * 2);
      ctx.fill();

      // Small trail
      const trailAngle = angle - 0.3;
      const tx = sx + Math.cos(trailAngle) * orbitR;
      const ty = sy + Math.sin(trailAngle) * orbitR * isoRatio;
      ctx.fillStyle = `rgba(80, 150, 255, ${alpha * 0.4})`;
      ctx.beginPath();
      ctx.arc(tx, ty, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  private renderLabel(ctx: CanvasRenderingContext2D, sx: number, sy: number, R: number, active: boolean): void {
    const labelY = sy + R * 0.45 + 18;

    if (active) {
      ctx.fillStyle = '#64b4ff';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(t('waypoint.label'), sx, labelY);

      ctx.fillStyle = '#8ac4ff';
      ctx.font = '10px Arial';
      ctx.fillText(this.name, sx, labelY + 15);
    } else {
      ctx.fillStyle = '#666666';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(t('waypoint.label'), sx, labelY);

      ctx.fillStyle = '#555555';
      ctx.font = '10px Arial';
      ctx.fillText(this.name, sx, labelY + 15);
    }
  }

  isPlayerNear(playerX: number, playerY: number, threshold: number = 60): boolean {
    const dx = playerX - this.x;
    const dy = playerY - this.y;
    return Math.sqrt(dx * dx + dy * dy) < threshold;
  }
}
